// engine/fx.ts — screenshake, hitstop routing, hit-flash timing, floating text,
// damage vignette. All pooled; nothing here allocates per frame.

import type { Loop } from './loop'

const MAX_FLOATS = 48

class FloatText {
  x = 0
  y = 0
  vy = -34
  life = 0
  maxLife = 0.8
  text = ''
  color = '#fff'
  size = 12
}

const floats: FloatText[] = []
for (let i = 0; i < MAX_FLOATS; i++) floats.push(new FloatText())
let floatCount = 0

class Fx {
  shakeMag = 0
  shakeTime = 0
  shakeDur = 0.001
  offX = 0
  offY = 0
  vigTime = 0
  vigDur = 0.001
  vigColor = '#ff2a2a'
  /** Player-facing settings multipliers (from SaveData.settings). */
  shakeScale = 1
  showDamageNumbers = false

  private loop: Loop | null = null
  private t = 0

  attachLoop(l: Loop): void {
    this.loop = l
  }

  reset(): void {
    this.shakeMag = 0
    this.shakeTime = 0
    this.offX = 0
    this.offY = 0
    this.vigTime = 0
    floatCount = 0
  }

  shake(intensity: number, seconds: number): void {
    const mag = intensity * this.shakeScale
    if (mag <= 0 || seconds <= 0) return
    if (mag >= this.shakeMag || this.shakeTime <= 0) {
      this.shakeMag = mag
      this.shakeDur = seconds
      this.shakeTime = seconds
    } else {
      this.shakeTime = Math.max(this.shakeTime, seconds * 0.6)
    }
  }

  hitstop(seconds: number): void {
    this.loop?.addHitstop(seconds)
  }

  vignette(seconds: number, color = '#ff2a2a'): void {
    this.vigTime = seconds
    this.vigDur = seconds
    this.vigColor = color
  }

  floatText(text: string, x: number, y: number, color = '#ffffff', size = 12): void {
    if (floatCount >= MAX_FLOATS) floatCount = MAX_FLOATS - 1
    const f = floats[floatCount++]
    f.x = x
    f.y = y
    f.vy = -34
    f.maxLife = 0.75
    f.life = f.maxLife
    f.text = text
    f.color = color
    f.size = size
  }

  damageNumber(n: number, x: number, y: number, color = '#ffe9a8'): void {
    if (!this.showDamageNumbers) return
    this.floatText(String(Math.round(n)), x, y, color, 11)
  }

  update(dt: number): void {
    this.t += dt
    if (this.shakeTime > 0) {
      this.shakeTime -= dt
      const k = Math.max(0, this.shakeTime / this.shakeDur)
      const m = this.shakeMag * k * k
      this.offX = Math.sin(this.t * 61.3) * m
      this.offY = Math.cos(this.t * 47.7) * m
    } else {
      this.offX = 0
      this.offY = 0
      this.shakeMag = 0
    }
    if (this.vigTime > 0) this.vigTime -= dt

    for (let i = floatCount - 1; i >= 0; i--) {
      const f = floats[i]
      f.life -= dt
      if (f.life <= 0) {
        const last = floats[floatCount - 1]
        floats[floatCount - 1] = f
        floats[i] = last
        floatCount--
        continue
      }
      f.y += f.vy * dt
      f.vy *= Math.pow(0.9, dt * 60)
    }
  }

  /** Drawn inside the world transform. */
  renderWorld(ctx: CanvasRenderingContext2D): void {
    if (floatCount === 0) return
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    for (let i = 0; i < floatCount; i++) {
      const f = floats[i]
      const a = Math.min(1, f.life / (f.maxLife * 0.6))
      ctx.globalAlpha = a
      ctx.font = `700 ${f.size}px ui-monospace, monospace`
      ctx.fillStyle = '#000000'
      ctx.fillText(f.text, f.x + 1, f.y + 1)
      ctx.fillStyle = f.color
      ctx.fillText(f.text, f.x, f.y)
    }
    ctx.globalAlpha = 1
  }

  /** Drawn in logical screen space, after the world. */
  renderScreen(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    if (this.vigTime <= 0) return
    const k = Math.max(0, this.vigTime / this.vigDur)
    const g = ctx.createRadialGradient(w / 2, h / 2, h * 0.28, w / 2, h / 2, h * 0.78)
    g.addColorStop(0, 'rgba(0,0,0,0)')
    g.addColorStop(1, this.vigColor)
    ctx.globalAlpha = 0.55 * k
    ctx.fillStyle = g
    ctx.fillRect(0, 0, w, h)
    ctx.globalAlpha = 1
  }
}

export const fx = new Fx()
